import { useMemo, useState } from "react";
import { Layout } from "@/components/layout";
import {
  useGetMe,
  useListBankAccounts,
  useCreateBankAccount,
  useListBankAccountMapping,
  useAssignBankSlot,
  useUnassignBankSlot,
  useBurnBankVariant,
  useImportBanking,
  useListBankingSources,
  useListBankingAudit,
  getListBankAccountsQueryKey,
  getListBankAccountMappingQueryKey,
  getListBankingAuditQueryKey,
  type BankAccount,
  type BankVariant,
  type BankingSource,
  type BankingAuditEntry,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Copy, Download, Flame, Loader2, Plus, Unlock, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { showBurnSummary } from "@/lib/burn-summary";
import {
  exportToCsv,
  buildBankAccountsExportRows,
  bankAccountsExportFilename,
} from "@/lib/csv-export";

function CopyableNumber({
  value,
  last4,
}: {
  value?: string | null;
  last4?: string | null;
}) {
  const [copied, setCopied] = useState(false);
  if (!value) {
    return (
      <span className="text-muted-foreground">
        {last4 ? `••${last4}` : "-"}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="group inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors"
      title="Click to copy"
    >
      <span className="tabular-nums">{value}</span>
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3 opacity-0 group-hover:opacity-60" />
      )}
    </button>
  );
}

function statusBadge(status: string) {
  switch (status) {
    case "available":
      return (
        <Badge variant="outline" className="border-green-500/30 text-green-500 bg-green-500/10 font-mono text-[9px] uppercase">
          Available
        </Badge>
      );
    case "assigned":
      return (
        <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/10 font-mono text-[9px] uppercase">
          Assigned
        </Badge>
      );
    case "used_existing":
      return (
        <Badge variant="outline" className="border-amber-500/30 text-amber-500 bg-amber-500/10 font-mono text-[9px] uppercase">
          Used
        </Badge>
      );
    case "burned":
      return (
        <Badge variant="outline" className="border-red-500/40 text-red-500 bg-red-500/10 font-mono text-[9px] uppercase">
          Burned
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="border-border text-muted-foreground font-mono text-[9px] uppercase">
          {status}
        </Badge>
      );
  }
}

export default function Banking() {
  const { data: me } = useGetMe();
  const isOperator = me?.role === "admin" || me?.role === "operator";

  return (
    <Layout>
      <div className="flex flex-col h-full w-full bg-background p-2 sm:p-4 overflow-hidden">
        <div className="mb-4 shrink-0 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-medium text-foreground">Banking</h1>
            <p className="text-xs text-muted-foreground">
              Physical bank accounts, variant slots, and inventory assignment
            </p>
          </div>
          {isOperator && <AddBankAccountDialog />}
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-card border border-border rounded-md">
          <Tabs defaultValue="accounts" className="flex-1 min-h-0 flex flex-col w-full">
            <div className="border-b border-border bg-muted p-2 shrink-0">
              <TabsList className="bg-transparent justify-start h-auto w-full flex-wrap gap-2 p-0">
                <TabsTrigger value="accounts" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Bank Accounts</TabsTrigger>
                <TabsTrigger value="mapping" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Mapping</TabsTrigger>
                {isOperator && (
                  <TabsTrigger value="import" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Import</TabsTrigger>
                )}
                <TabsTrigger value="audit" className="text-xs h-8 px-4 data-[state=active]:bg-secondary border border-transparent data-[state=active]:border-border rounded">Audit Log</TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 min-h-0 overflow-auto custom-scrollbar p-2 sm:p-4">
              <TabsContent value="accounts" className="m-0">
                <BankAccountsTab />
              </TabsContent>
              <TabsContent value="mapping" className="m-0">
                <MappingTab isOperator={isOperator} />
              </TabsContent>
              {isOperator && (
                <TabsContent value="import" className="m-0">
                  <ImportTab />
                </TabsContent>
              )}
              <TabsContent value="audit" className="m-0">
                <AuditTab />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}

function BankAccountsTab() {
  const [search, setSearch] = useState("");
  const { data: accounts, isLoading } = useListBankAccounts(
    search ? { search } : undefined,
  );
  const list = accounts ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="h-8 w-64 text-xs"
          placeholder="Search card name, bank, source…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs px-2.5 rounded-full ml-auto"
          disabled={list.length === 0}
          title={
            list.length === 0
              ? "No bank accounts to export"
              : `Export ${list.length} bank account${list.length === 1 ? "" : "s"} to CSV`
          }
          onClick={() =>
            exportToCsv(
              buildBankAccountsExportRows(list),
              bankAccountsExportFilename(),
            )
          }
        >
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <div className="border border-border rounded overflow-x-auto custom-scrollbar">
          <table className="w-full text-xs min-w-[560px]">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Key</th>
                <th className="text-left py-2 px-3 font-medium">Card Name</th>
                <th className="text-left py-2 px-3 font-medium">Source</th>
                <th className="text-left py-2 px-3 font-medium">Bank</th>
                <th className="text-left py-2 px-3 font-medium">Type</th>
                <th className="text-left py-2 px-3 font-medium">Routing</th>
                <th className="text-left py-2 px-3 font-medium">Account</th>
                <th className="text-left py-2 px-3 font-medium">Slots</th>
              </tr>
            </thead>
            <tbody>
              {(accounts ?? []).map((a: BankAccount) => (
                <tr key={a.id} className="border-t border-border hover:bg-muted/40">
                  <td className="py-1.5 px-3 font-mono">{a.bankAccountKey}</td>
                  <td className="py-1.5 px-3">{a.cardName || "-"}</td>
                  <td className="py-1.5 px-3">{a.source || "-"}</td>
                  <td className="py-1.5 px-3">{a.bank || "-"}</td>
                  <td className="py-1.5 px-3">{a.accountType || "-"}</td>
                  <td className="py-1.5 px-3 font-mono"><CopyableNumber value={a.routingNumber} last4={a.routingNumberLast4} /></td>
                  <td className="py-1.5 px-3 font-mono"><CopyableNumber value={a.accountNumber} last4={a.accountNumberLast4} /></td>
                  <td className="py-1.5 px-3">
                    <span className={a.isFull ? "text-amber-500" : "text-foreground"}>
                      {a.allocationCount}/{a.capacity}
                    </span>
                    {a.availableVariants && a.availableVariants.length > 0 && (
                      <span className="ml-2 text-[10px] text-muted-foreground font-mono">
                        free: {a.availableVariants.join(",")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {accounts && accounts.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">No bank accounts. Import a CSV or add one manually.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MappingTab({ isOperator }: { isOperator: boolean }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const params = useMemo(() => {
    const p: { status?: "available" | "assigned" | "used_existing" | "burned"; search?: string } = {};
    if (status !== "all") p.status = status as "available" | "assigned" | "used_existing" | "burned";
    if (search) p.search = search;
    return Object.keys(p).length ? p : undefined;
  }, [status, search]);
  const { data: rows, isLoading } = useListBankAccountMapping(params);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getListBankAccountMappingQueryKey() });
    qc.invalidateQueries({ queryKey: getListBankAccountsQueryKey() });
    qc.invalidateQueries({ queryKey: getListBankingAuditQueryKey() });
  };

  const unassign = useUnassignBankSlot({
    mutation: {
      onSuccess: () => { invalidate(); toast.success("Slot released"); },
      onError: () => toast.error("Failed to release slot"),
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="used_existing">Used</SelectItem>
            <SelectItem value="burned">Burned</SelectItem>
          </SelectContent>
        </Select>
        <Input
          className="h-8 w-64 text-xs"
          placeholder="Search email, card, key…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isOperator && <AssignSlotForm onAssigned={invalidate} />}
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <div className="border border-border rounded overflow-x-auto custom-scrollbar">
          <table className="w-full text-xs min-w-[560px]">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Key</th>
                <th className="text-left py-2 px-3 font-medium">Variant</th>
                <th className="text-left py-2 px-3 font-medium">Card Name</th>
                <th className="text-left py-2 px-3 font-medium">Status</th>
                <th className="text-left py-2 px-3 font-medium">Assigned Email</th>
                <th className="text-left py-2 px-3 font-medium">Source</th>
                {isOperator && <th className="text-right py-2 px-3 font-medium">Action</th>}
              </tr>
            </thead>
            <tbody>
              {(rows ?? []).map((v: BankVariant) => (
                <tr key={v.id} className="border-t border-border hover:bg-muted/40">
                  <td className="py-1.5 px-3 font-mono">{v.bankAccountKey}</td>
                  <td className="py-1.5 px-3 font-mono">{v.variant}</td>
                  <td className="py-1.5 px-3">{v.cardName || "-"}</td>
                  <td className="py-1.5 px-3">{statusBadge(v.allocationStatus)}</td>
                  <td className="py-1.5 px-3">{v.inventoryAccountEmail || <span className="text-muted-foreground">—</span>}</td>
                  <td className="py-1.5 px-3 text-muted-foreground">{v.assignmentSource || "-"}</td>
                  {isOperator && (
                    <td className="py-1.5 px-3 text-right">
                      {v.allocationStatus === "assigned" ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[10px] rounded-full text-emerald-500"
                            disabled={unassign.isPending}
                            onClick={() => unassign.mutate({ id: v.id })}
                          >
                            <Unlock className="h-3 w-3 mr-0.5" /> Release
                          </Button>
                          <BurnVariantDialog variant={v} onBurned={invalidate} />
                        </div>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
              {rows && rows.length === 0 && (
                <tr><td colSpan={isOperator ? 7 : 6} className="text-center py-6 text-muted-foreground">No mapping rows.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AssignSlotForm({ onAssigned }: { onAssigned: () => void }) {
  const [email, setEmail] = useState("");
  const assign = useAssignBankSlot({
    mutation: {
      onSuccess: (res) => {
        if (res.assigned && res.slot) {
          toast.success(`Assigned ${res.slot.bankAccountKey}-${res.slot.variant}`);
          setEmail("");
          onAssigned();
        } else {
          toast.warning("No available slot to assign");
        }
      },
      onError: () => toast.error("Failed to assign slot"),
    },
  });
  return (
    <div className="flex items-center gap-2 ml-auto">
      <Input
        className="h-8 w-56 text-xs"
        placeholder="inventory account email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Button
        size="sm"
        className="h-8"
        disabled={!email.trim() || assign.isPending}
        onClick={() => assign.mutate({ data: { email: email.trim() } })}
      >
        {assign.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        <span className="ml-1">Assign Slot</span>
      </Button>
    </div>
  );
}

export function BurnVariantDialog({
  variant,
  onBurned,
}: {
  variant: BankVariant;
  onBurned: () => void;
}) {
  const [open, setOpen] = useState(false);
  const burn = useBurnBankVariant({
    mutation: {
      onSuccess: (res) => {
        setOpen(false);
        showBurnSummary(res);
        onBurned();
      },
      onError: () => toast.error("Failed to burn bank account"),
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 text-[10px] rounded-full border-red-500/40 text-red-500 hover:bg-red-500/10 hover:text-red-500"
        >
          <Flame className="h-3 w-3 mr-0.5" />
          Burn {variant.cardName || variant.bankAccountKey}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Burn bank account {variant.bankAccountKey}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <img
            src={`${import.meta.env.BASE_URL}burned-tm-error.png`}
            alt="Ticketmaster duplicate-account error"
            className="w-full rounded border border-border"
          />
          <p className="text-xs text-muted-foreground">
            Confirm you have seen the Ticketmaster error shown above before
            proceeding. Ticketmaster rejects the whole routing number, so this
            retires <span className="font-medium text-foreground">every slot</span> on
            bank account{" "}
            <span className="font-mono text-foreground">{variant.bankAccountKey}</span> and
            moves the accounts on it to <span className="font-medium text-foreground">new
            bank accounts</span>.
          </p>
          <p className="text-[11px] text-muted-foreground">
            Any account that is already listed or sold is left in place so its
            listing/sale link is preserved. Every burned slot keeps its account
            link for future transaction matching.
          </p>
        </div>
        <DialogFooter>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} disabled={burn.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={burn.isPending}
            onClick={() => burn.mutate({ variantId: variant.id })}
          >
            {burn.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Flame className="h-3.5 w-3.5 mr-1" />}
            Confirm &amp; Burn Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddBankAccountDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: sources } = useListBankingSources();
  const [form, setForm] = useState({
    source: "",
    bank: "",
    accountType: "",
    cardName: "",
    routingNumber: "",
    accountNumber: "",
  });

  const create = useCreateBankAccount({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListBankAccountsQueryKey() });
        qc.invalidateQueries({ queryKey: getListBankAccountMappingQueryKey() });
        toast.success("Bank account added with 4 open slots");
        setOpen(false);
        setForm({ source: "", bank: "", accountType: "", cardName: "", routingNumber: "", accountNumber: "" });
      },
      onError: () => toast.error("Failed to add bank account"),
    },
  });

  const enabledSources = (sources ?? []).filter((s: BankingSource) => s.enabled);

  const applySource = (name: string) => {
    const s = enabledSources.find((x) => x.name === name);
    setForm((f) => ({
      ...f,
      source: name,
      bank: s?.bank ?? f.bank,
      accountType: s?.accountType ?? f.accountType,
    }));
  };

  const submit = () => {
    if (!form.routingNumber.trim() || !form.accountNumber.trim()) {
      toast.error("Routing and account numbers are required");
      return;
    }
    create.mutate({
      data: {
        source: form.source || undefined,
        bank: form.bank || undefined,
        accountType: form.accountType || undefined,
        cardName: form.cardName || undefined,
        routingNumber: form.routingNumber.trim(),
        accountNumber: form.accountNumber.trim(),
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-8">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Bank Account
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Bank Account</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Source</Label>
            <Select value={form.source} onValueChange={applySource}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                {enabledSources.map((s) => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Bank</Label>
              <Input className="h-8 text-xs" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Account Type</Label>
              <Input className="h-8 text-xs" value={form.accountType} onChange={(e) => setForm({ ...form, accountType: e.target.value })} placeholder="checking / savings" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Card Name (optional — auto-generated if blank)</Label>
            <Input className="h-8 text-xs" value={form.cardName} onChange={(e) => setForm({ ...form, cardName: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Routing Number</Label>
              <Input className="h-8 text-xs font-mono" value={form.routingNumber} onChange={(e) => setForm({ ...form, routingNumber: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Account Number</Label>
              <Input className="h-8 text-xs font-mono" value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Routing and account numbers are encrypted at rest and shown in full to signed-in operators.
            Four open variant slots (A–D) are created automatically.
          </p>
        </div>
        <DialogFooter>
          <Button size="sm" onClick={submit} disabled={create.isPending}>
            {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
            Add Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportTab() {
  const qc = useQueryClient();
  const [accountsCsv, setAccountsCsv] = useState("");
  const [mappingCsv, setMappingCsv] = useState("");

  const importMut = useImportBanking({
    mutation: {
      onSuccess: (summary) => {
        qc.invalidateQueries({ queryKey: getListBankAccountsQueryKey() });
        qc.invalidateQueries({ queryKey: getListBankAccountMappingQueryKey() });
        qc.invalidateQueries({ queryKey: getListBankingAuditQueryKey() });
        toast.success(
          `Imported ${summary.bankAccountsUpserted} accounts, ${summary.variantsUpserted} slots (${summary.resolvedAccounts} resolved)`,
        );
      },
      onError: () => toast.error("Import failed — check CSV format"),
    },
  });

  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-xs text-muted-foreground">
        Paste the contents of your bank-accounts and bank-account-mapping CSV exports.
        Imports are idempotent — re-running updates existing rows by key.
      </p>
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Bank Accounts CSV</Label>
        <textarea
          className="w-full h-40 text-xs font-mono bg-background border border-border rounded p-2 custom-scrollbar"
          value={accountsCsv}
          onChange={(e) => setAccountsCsv(e.target.value)}
          placeholder="bank_account_key,source,bank,card_name,routing_number,account_number,..."
        />
      </div>
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Bank Account Mapping CSV</Label>
        <textarea
          className="w-full h-40 text-xs font-mono bg-background border border-border rounded p-2 custom-scrollbar"
          value={mappingCsv}
          onChange={(e) => setMappingCsv(e.target.value)}
          placeholder="bank_account_key,variant,inventory_account_email,allocation_status,..."
        />
      </div>
      <Button
        size="sm"
        disabled={!accountsCsv.trim() || !mappingCsv.trim() || importMut.isPending}
        onClick={() => importMut.mutate({ data: { bankAccountsCsv: accountsCsv, bankAccountMappingCsv: mappingCsv } })}
      >
        {importMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
        Import
      </Button>
    </div>
  );
}

function AuditTab() {
  const { data: entries, isLoading } = useListBankingAudit();
  return isLoading ? (
    <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
  ) : (
    <div className="border border-border rounded overflow-x-auto custom-scrollbar">
      <table className="w-full text-xs min-w-[560px]">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            <th className="text-left py-2 px-3 font-medium">When</th>
            <th className="text-left py-2 px-3 font-medium">Actor</th>
            <th className="text-left py-2 px-3 font-medium">Action</th>
            <th className="text-left py-2 px-3 font-medium">Mapping</th>
            <th className="text-left py-2 px-3 font-medium">Bank Account</th>
          </tr>
        </thead>
        <tbody>
          {(entries ?? []).map((e: BankingAuditEntry) => (
            <tr key={e.id} className="border-t border-border hover:bg-muted/40">
              <td className="py-1.5 px-3 text-muted-foreground">
                {e.createdAt ? new Date(e.createdAt).toLocaleString() : "-"}
              </td>
              <td className="py-1.5 px-3">{e.actorEmail || "system"}</td>
              <td className="py-1.5 px-3 font-mono">{e.action}</td>
              <td className="py-1.5 px-3 font-mono text-muted-foreground">{e.mappingId ?? "-"}</td>
              <td className="py-1.5 px-3 font-mono text-muted-foreground">{e.bankAccountId ?? "-"}</td>
            </tr>
          ))}
          {entries && entries.length === 0 && (
            <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No banking activity yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
